// API helpers for the Predictions quest.
//
// There are no accounts here — a set's admin code is the only credential. Codes live in
// sessionStorage so a refresh doesn't lock you out mid-edit, but close the tab and they're gone.
// They ride in the X-Admin-Code header rather than the query string, which would put them in the
// server's access log.

const STORAGE_KEY = 'predictions:codes';
const FALLBACK = '*'; // last code used anywhere, tried when a set has no code of its own

function readCodes() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function writeCodes(codes) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(codes));
  } catch {
    // Private browsing with storage disabled — codes just won't survive a refresh.
  }
}

export function getCode(slug) {
  const codes = readCodes();
  return (slug && codes[slug]) || codes[FALLBACK] || '';
}

export function rememberCode(slug, code) {
  const codes = readCodes();
  if (slug) codes[slug] = code;
  codes[FALLBACK] = code;
  writeCodes(codes);
}

export function forgetCode(slug) {
  const codes = readCodes();
  if (slug) delete codes[slug];
  delete codes[FALLBACK];
  writeCodes(codes);
}

async function request(path, { method = 'GET', body, code } = {}) {
  const headers = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (code) headers['X-Admin-Code'] = code;

  const res = await fetch(`/api/predictions${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body)
  });

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { error: text };
    }
  }

  if (!res.ok) {
    const error = new Error((data && data.error) || `Request failed (${res.status})`);
    error.status = res.status;
    error.isPrivate = !!(data && data.private);
    throw error;
  }
  return data;
}

export const api = {
  listSets: code => request(`/sets`, { code }),
  aggregate: (code, categories = []) => {
    const params = new URLSearchParams();
    for (const name of categories) params.append('category', name);
    const query = params.toString();
    return request(`/aggregate${query ? `?${query}` : ''}`, { code });
  },
  createSet: payload => request(`/sets`, { method: 'POST', body: payload }),
  verify: (slug, code) => request(`/sets/${slug}/verify`, { method: 'POST', body: { adminCode: code } }),
  getSet: (slug, code) => request(`/sets/${slug}`, { code }),
  updateSet: (slug, code, payload) =>
    request(`/sets/${slug}`, { method: 'PATCH', code, body: { adminCode: code, ...payload } }),
  deleteSet: (slug, code) => request(`/sets/${slug}`, { method: 'DELETE', code, body: { adminCode: code } }),

  addCategory: (slug, code, name) =>
    request(`/sets/${slug}/categories`, { method: 'POST', code, body: { adminCode: code, name } }),
  updateCategory: (slug, code, id, payload) =>
    request(`/sets/${slug}/categories/${id}`, { method: 'PATCH', code, body: { adminCode: code, ...payload } }),
  deleteCategory: (slug, code, id) =>
    request(`/sets/${slug}/categories/${id}`, { method: 'DELETE', code, body: { adminCode: code } }),

  addPrediction: (slug, code, payload) =>
    request(`/sets/${slug}/predictions`, { method: 'POST', code, body: { adminCode: code, ...payload } }),
  addPredictions: (slug, code, rows, categoryId) =>
    request(`/sets/${slug}/predictions/bulk`, { method: 'POST', code, body: { adminCode: code, rows, categoryId } }),
  updatePrediction: (slug, code, id, payload) =>
    request(`/sets/${slug}/predictions/${id}`, { method: 'PATCH', code, body: { adminCode: code, ...payload } }),
  updatePredictions: (slug, code, updates) =>
    request(`/sets/${slug}/predictions`, { method: 'PATCH', code, body: { adminCode: code, updates } }),
  deletePrediction: (slug, code, id) =>
    request(`/sets/${slug}/predictions/${id}`, { method: 'DELETE', code, body: { adminCode: code } })
};

export function exportUrl(slug, code) {
  // A download navigates rather than fetches, so this one endpoint has to take the code on the URL.
  return `/api/predictions/sets/${slug}/export.csv${code ? `?code=${encodeURIComponent(code)}` : ''}`;
}
